import { useCallback, useEffect, useState } from "react";
import {
  listSessions, createSession, getSession, deleteSession, listChanges, listSources,
  type Session,
} from "../api";
import { canManageClients, canWrite, type Me } from "../auth";
import { ChatView } from "../components/ChatView";
import { SynapsePreview } from "../components/SynapsePreview";
import { PipelineRunPanel } from "../components/PipelineRunPanel";
import {
  forgetSession, getChat, sessionKey, updateChat, updatePanel, usePanel,
} from "../sessionStore";
import { Resizer } from "../components/Resizer";
import { IngestModal } from "../components/IngestModal";
import { ReviewPanel } from "../components/ReviewPanel";
import { SkillsPanel } from "../components/SkillsPanel";
import { MembersPanel } from "../components/MembersPanel";
import { ClientsPanel } from "../components/ClientsPanel";
import { SourcesPanel } from "../components/SourcesPanel";
import { Sidebar } from "../components/Sidebar";
import { skinFor, hostLockedInstance } from "../skins";
import { PipelineView } from "../components/PipelineView";
import { PanelWindow } from "../components/PanelWindow";
import { IconMenu, IconPage, IconPipelines } from "../components/icons";


/**
 * Instance page = chat shell: nav sidebar + chat main +
 * right-hand panel stack.
 *
 * The right column is per-session and content-driven: each session remembers the
 * synapse page it last opened and the pipeline run it kicked off, and the column
 * itself disappears from the grid when that session has nothing open. A restore
 * rail brings back whatever the session still holds.
 *
 * Role gating: a read+chat ("member") account gets a chat-only shell — no
 * skills, pipelines, review, members, ingest, or source affordances. Those are
 * for writers + owners. The backend enforces the same (defense in depth); this
 * just keeps the UI honest so members never see capabilities they don't have.
 */

interface Props {
  instance: string;
  sid?: string;
  onNavigate: (to: string) => void;
  me: Me;
}

// Column widths persist across sessions so the layout the user drags to sticks.
const SIDEBAR_DEFAULT = 264;
const PREVIEW_DEFAULT = 340;
const SIDEBAR_KEY = "nexus.layout.sidebarW";
const PREVIEW_KEY = "nexus.layout.previewW";
const RAIL_KEY = "nexus.layout.rail";

function loadWidth(key: string, fallback: number): number {
  const v = Number(localStorage.getItem(key));
  return Number.isFinite(v) && v > 0 ? v : fallback;
}


export function Instance({ instance, sid, onNavigate, me }: Props) {
  const skin = skinFor(instance);
  const [sidebarW, setSidebarW] = useState(() => loadWidth(SIDEBAR_KEY, SIDEBAR_DEFAULT));
  const [previewW, setPreviewW] = useState(() => loadWidth(PREVIEW_KEY, PREVIEW_DEFAULT));
  const [rail, setRail] = useState(() => localStorage.getItem(RAIL_KEY) === "1");
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { localStorage.setItem(SIDEBAR_KEY, String(sidebarW)); }, [sidebarW]);
  useEffect(() => { localStorage.setItem(PREVIEW_KEY, String(previewW)); }, [previewW]);
  useEffect(() => { localStorage.setItem(RAIL_KEY, rail ? "1" : "0"); }, [rail]);
  // Navigating closes the phone drawer — it never sits over the answer.
  useEffect(() => { setDrawer(false); }, [sid]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [changeCount, setChangeCount] = useState(0);
  const [archetype, setArchetype] = useState<"prose" | "code" | null>(null);

  const writeable = canWrite(me, instance);
  const manageClients = canManageClients(me, instance);

  // Right-column state belongs to the session. With no chat open it belongs
  // to the instance's home page, so Pipelines opens without a chat.
  const skey = sessionKey(instance, sid || "~home");
  const panel = usePanel(skey);
  const setPanel = (patch: Parameters<typeof updatePanel>[1]) => updatePanel(skey, patch);
  const openCitation = (pageId: string, anchor?: string) => {
    setPanel({ previewPageId: pageId, previewAnchor: anchor, previewOpen: true });
  };

  const pipelinesOpen = writeable && panel.pipelinesOpen;
  const runOpen = panel.runOpen && !!panel.run;
  const rightOpen = panel.previewOpen || runOpen || pipelinesOpen;
  // What this session could put back on screen once the column is closed.
  const restorable = [
    panel.previewPageId && !panel.previewOpen
      ? {
          key: "synapse", label: "synapse preview",
          icon: <IconPage size={15} />, open: () => setPanel({ previewOpen: true }),
        }
      : null,
    panel.run && !panel.runOpen
      ? {
          key: "run", label: "pipeline run",
          icon: <IconPipelines size={15} />, open: () => setPanel({ runOpen: true }),
        }
      : null,
  ].filter(Boolean) as { key: string; label: string; icon: JSX.Element; open: () => void }[];

  // load session list (server returns only the caller's own sessions)
  const refreshSessions = useCallback(async () => {
    try {
      const r = await listSessions(instance);
      setSessions(r.sessions);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [instance]);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  const refreshChanges = useCallback(async () => {
    if (!writeable) return;
    try {
      const { changes } = await listChanges(instance);
      setChangeCount(changes.length);
    } catch { /* non-fatal — review gate may not be deployed yet */ }
  }, [instance, writeable]);
  useEffect(() => { refreshChanges(); }, [refreshChanges]);

  // Archetype gates the Sources panel (code repos only). Cheap read — the
  // live webhook/PR status is fetched lazily by the panel itself.
  useEffect(() => {
    if (!writeable) { setArchetype(null); return; }
    listSources(instance)
      .then(d => setArchetype(d.archetype))
      .catch(() => setArchetype(null));
  }, [instance, writeable]);

  // Load the session and seed its transcript. The fetch only replaces what we
  // already hold when it is at least as complete — a run still streaming, or a
  // turn the server hasn't persisted yet, is newer than any refetch.
  useEffect(() => {
    if (!sid) {
      setSession(null);
      return;
    }
    const key = sessionKey(instance, sid);
    let stale = false;
    getSession(instance, sid)
      .then(r => {
        if (stale) return;
        setSession(r.session);
        const cur = getChat(key);
        const canonical = !cur.busy && r.messages.length >= cur.messages.length;
        updateChat(key, canonical ? { loaded: true, messages: r.messages } : { loaded: true });
      })
      .catch(e => { if (!stale) setErr((e as Error).message); });
    return () => { stale = true; };
  }, [instance, sid]);

  const handleNewChat = async () => {
    try {
      const { session } = await createSession(instance);
      await refreshSessions();
      onNavigate(`/${instance}/${session.id}`);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const handleDelete = async (s: Session) => {
    if (!window.confirm(`Delete "${s.title || "this chat"}"? This cannot be undone.`)) return;
    try {
      await deleteSession(instance, s.id);
      forgetSession(sessionKey(instance, s.id));
      await refreshSessions();
      if (sid === s.id) {
        setSession(null);
        onNavigate(`/${instance}`);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div
      className={
        `shell${rightOpen ? "" : " no-right"}${rail ? " rail" : ""}${drawer ? " drawer-open" : ""}`
      }
      style={{ "--sidebar-w": `${sidebarW}px`, "--preview-w": `${previewW}px` } as React.CSSProperties}
    >
      {writeable && (
        <>
          <IngestModal
            open={ingestOpen}
            instance={instance}
            onClose={() => setIngestOpen(false)}
            onIngested={() => {
              setIngestOpen(false);
              refreshChanges();
              setReviewOpen(true);
            }}
          />

          <ReviewPanel
            open={reviewOpen}
            instance={instance}
            onClose={() => { setReviewOpen(false); refreshChanges(); }}
            onApprovedReload={() => window.location.reload()}
          />

          <SkillsPanel
            open={skillsOpen}
            instance={instance}
            onOpenPipeline={() => { setSkillsOpen(false); setPanel({ pipelinesOpen: true }); }}
            onClose={() => { setSkillsOpen(false); refreshChanges(); }}
          />

          <MembersPanel
            open={membersOpen}
            instance={instance}
            onClose={() => setMembersOpen(false)}
          />

          <ClientsPanel
            open={clientsOpen}
            instance={instance}
            onClose={() => setClientsOpen(false)}
          />

          <SourcesPanel
            open={sourcesOpen}
            instance={instance}
            onClose={() => setSourcesOpen(false)}
          />
        </>
      )}

      <Sidebar
        instance={instance}
        skin={skin}
        sessions={sessions}
        currentSid={sid}
        collapsed={rail}
        writeable={writeable}
        changeCount={changeCount}
        showSources={archetype === "code"}
        showSource={!hostLockedInstance()}
        pipelinesActive={!!pipelinesOpen}
        onToggleCollapse={() => setRail(r => !r)}
        onNewChat={handleNewChat}
        onSelect={(s) => onNavigate(`/${instance}/${s.id}`)}
        onDelete={handleDelete}
        onSkills={() => setSkillsOpen(true)}
        onPipelines={() => setPanel(p => ({ pipelinesOpen: !p.pipelinesOpen }))}
        onSourcesPanel={() => setSourcesOpen(true)}
        onChanges={() => setReviewOpen(true)}
        onMembers={() => setMembersOpen(true)}
        onClients={manageClients ? () => setClientsOpen(true) : undefined}
        onIngest={() => setIngestOpen(true)}
      />
      {drawer && (
        <button
          className="sidebar-scrim"
          onClick={() => setDrawer(false)}
          aria-label="Close navigation"
        />
      )}

      <Resizer
        side="left"
        className="rz-left"
        width={sidebarW}
        onResize={setSidebarW}
        min={180}
        max={480}
        defaultWidth={SIDEBAR_DEFAULT}
        label="Resize chat history"
      />

      <main className={`main${!rightOpen && restorable.length > 0 ? " has-rail" : ""}`}>
        <header className="chat-header">
          <button
            className="drawer-btn ghost"
            onClick={() => setDrawer(true)}
            title="Open navigation"
            aria-label="Open navigation"
          >
            <IconMenu size={17} />
          </button>
          <span className="chat-title">{session?.title || "New chat"}</span>
          <div className="spacer" />
          <span className="instance-tag">{skin ? skin.tag : instance}</span>
        </header>

        {err && <div className="error-banner">{err}</div>}
        {!sid && (
          <div className="empty-state">
            <span className="empty-mark"><IconPage size={24} /></span>
            <h2>Ask the {skin ? skin.brand : "Nexus"} clerk</h2>
            <p className="empty-sub">
              Answers are drawn from this instance's synapse, with every claim
              cited back to the page it came from.
            </p>
            <button className="primary" onClick={handleNewChat}>New chat</button>
          </div>
        )}
        {sid && session && (
          <ChatView
            instance={instance}
            session={session}
            onCitationClick={openCitation}
            onAfterAsk={refreshSessions}
          />
        )}
        {!rightOpen && restorable.length > 0 && (
          <div className="restore-rail">
            {restorable.map(r => (
              <button key={r.key} onClick={r.open}
                      title={`Reopen ${r.label}`} aria-label={`Reopen ${r.label}`}>
                {r.icon}
              </button>
            ))}
          </div>
        )}
      </main>

      {rightOpen && (
        <Resizer
          side="right"
          className="rz-right"
          width={previewW}
          onResize={setPreviewW}
          min={240}
          max={680}
          defaultWidth={PREVIEW_DEFAULT}
          label="Resize side panel"
        />
      )}

      {rightOpen && (
        <aside className="preview-panel">
          {panel.previewOpen && (
            <PanelWindow title="Synapse preview" onClose={() => setPanel({ previewOpen: false })}>
              <SynapsePreview
                instance={instance}
                pageId={panel.previewPageId}
                anchor={panel.previewAnchor}
                onOpenPage={(id) => openCitation(id)}
              />
            </PanelWindow>
          )}
          {runOpen && panel.run && (
            <PanelWindow title="Pipeline run" onClose={() => setPanel({ runOpen: false })}>
              <PipelineRunPanel run={panel.run} />
            </PanelWindow>
          )}
          {pipelinesOpen && (
            <PanelWindow title="Pipelines" onClose={() => setPanel({ pipelinesOpen: false })}>
              <PipelineView instance={instance} />
            </PanelWindow>
          )}
        </aside>
      )}
    </div>
  );
}
